/**
 * The AI arm of onboarding: «Что вы поставляете?» → «Проверьте, что получилось».
 *
 * It replaces ONE step of the wizard — the category picker — and nothing else.
 * Everything around it (the step counter, the Telegram step, the ИНН step, the
 * gate that decides whether the modal opens at all) is untouched, and
 * `onManual` hands control straight back to the picker at any point. That is
 * the contract with the experiment: the manual branch stays whole, because the
 * AI is an addition to it and the flag can be turned off mid-week.
 *
 * The whole state machine lives here rather than in `onboarding-modal.tsx` so
 * the modal keeps its existing shape and its existing tests keep passing with
 * the flag off.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocaleNavigate } from '../i18n/locale-link';
import { useLocale } from '../i18n/locale-context';
import { countryConfig } from '../country';
import { AI_MAX_TEXT, reportOutcomeQuietly } from '../lib/ai-search-client';
import { useAiPropose } from '../lib/use-ai-propose';
import { countEdits, draftFromProposal, type AiDraft } from '../lib/ai-proposal-draft';
import { saveAiDraft } from '../lib/ai-save-search';
import type { NicheFirstMatch, SavedSearch } from '../lib/saved-search-client';
import { ApiError, NetworkError } from '../lib/api';
import { useAuthOptional } from '../lib/auth-context';
import { savedSearchCapFor } from '../lib/saved-search-cap';
import {
  AiErrorCard,
  AiNotUnderstoodCard,
  AiQuotaLine,
  AiQuotaOutCard,
  AiSparkle,
  aiPrimaryClass,
} from './ai-search-cards';
import { AiProposalReview } from './ai-proposal-review';
import { SavedSearchEditSheet } from './saved-search-edit-sheet';

const TIN_DIGITS = countryConfig.companyIdForm.digits;

/**
 * Enough to work with? Three significant words, or a complete company id.
 *
 * Below that the button is DISABLED rather than red: nothing is wrong yet, the
 * person is still typing. A validation error on a field someone has not
 * finished is how a form teaches people it dislikes them — and a refusal costs
 * nothing here, whereas sending «насос» to the model costs money and comes back
 * as «не понял», which reads like our failure rather than a short sentence.
 */
export function canAsk(text: string, tin: string): boolean {
  if (tin.length === TIN_DIGITS) return true;
  const words = text
    .trim()
    .split(/\s+/)
    .filter((w) => w.replace(/[^\p{L}\p{N}]/gu, '').length >= 2);
  return words.length >= 3;
}

export function AiOnboardingStep({
  titleId,
  onManual,
  onSaved,
}: {
  titleId: string;
  /** Hand over to the existing category picker (мокап 5: every card offers it). */
  onManual: (reason: 'link' | 'not_understood' | 'quota' | 'error') => void;
  /** A search now exists — the wizard moves on exactly as it does after a
   *  manual pick (invalidate, sample alert, next step). */
  onSaved: (search: SavedSearch, firstMatch: NicheFirstMatch | null) => void;
}) {
  const { t, locale } = useLocale();
  const navigate = useLocaleNavigate();
  // Optional: the step is also rendered (and unit-tested) outside AuthProvider.
  const user = useAuthOptional()?.user ?? null;
  const [text, setText] = useState('');
  const [tin, setTin] = useState('');
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The row to open the standard builder on, after «Изменить» saved it. */
  const [editTarget, setEditTarget] = useState<SavedSearch | null>(null);
  const [pendingFirstMatch, setPendingFirstMatch] = useState<NicheFirstMatch | null>(null);

  const ai = useAiPropose('onboarding', locale, true);
  const textRef = useRef<HTMLTextAreaElement>(null);

  /**
   * The company id alone found nothing to build on (`ai_tin_unknown`, Kazakhstan
   * only). That is a gap in OUR data, not something the person got wrong, so it
   * is not a failure card: step 1 stays, says so, and asks the question.
   */
  const tinUnknown = ai.phase === 'failed' && ai.failure === 'ai_tin_unknown';
  useEffect(() => {
    if (!tinUnknown) return;
    // The id has already told us everything it can — the next ask is the
    // description alone, and the button waits for it rather than re-sending
    // the same id for the same answer.
    setTin('');
    textRef.current?.focus();
  }, [tinUnknown]);

  function ask() {
    setDraft(null);
    setSaveError(null);
    ai.ask(tin.length === TIN_DIGITS ? { tin, text: text.trim() || undefined } : { text });
  }

  // Seed the editable draft the first time a proposal lands (and again for
  // each new one). Done on render rather than in an effect so the review never
  // paints one frame with no chips.
  const proposal = ai.phase === 'ready' ? ai.result : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (proposal && seededFor !== proposal.draftId) {
    setSeededFor(proposal.draftId);
    setDraft(draftFromProposal(proposal.proposal));
  }

  /**
   * What became of the draft, kept in a ref so the unmount reporter below can
   * read it without re-subscribing on every keystroke.
   */
  const fate = useRef<{ draftId: string | null; settled: boolean; edits: number }>({
    draftId: null,
    settled: false,
    edits: 0,
  });
  fate.current.draftId = proposal?.draftId ?? fate.current.draftId;
  if (proposal && draft) fate.current.edits = countEdits(proposal.proposal, draft);

  /**
   * A proposal the user walked away from is an OUTCOME, not an absence.
   *
   * Without this the drafts table would show «proposed» forever for everyone
   * who closed the sheet, and «доля принятых без правок» — the number this
   * whole experiment is judged on — would divide by a denominator that quietly
   * mixes "still deciding" with "did not want it".
   */
  useEffect(() => {
    return () => {
      if (!fate.current.draftId || fate.current.settled) return;
      reportOutcomeQuietly(fate.current.draftId, {
        outcome: 'abandoned',
        editsCount: fate.current.edits,
      });
    };
  }, []);

  /** Leaving for the manual picker closes the draft out honestly. */
  function leaveToManual(reason: 'link' | 'not_understood' | 'quota' | 'error') {
    if (fate.current.draftId && !fate.current.settled) {
      fate.current.settled = true;
      reportOutcomeQuietly(fate.current.draftId, {
        outcome: 'manual',
        editsCount: fate.current.edits,
      });
    }
    onManual(reason);
  }

  async function save(openBuilder: boolean) {
    if (!proposal || !draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      // `replace: true` marks the row `fromOnboarding` server-side, as the
      // manual picker's saves are. Unlike the manual niche-switch it does NOT
      // license retiring anything: with `aiDraftId` present the server refuses
      // at the free cap (`saved_search_limit`) instead of switching off the
      // reader's oldest search — see notifications.controller.
      const res = await saveAiDraft(draft, { draftId: proposal.draftId, replace: true });
      // The create endpoint closed the draft out itself (saved / edited_saved),
      // so the unmount reporter must not follow it with «abandoned».
      fate.current.settled = true;
      if (openBuilder) {
        // «Изменить»: the search exists, so the STANDARD builder can open on it
        // — prefilled with exactly this proposal, with every axis the sheet
        // supports. Onboarding advances when the sheet closes, not before.
        setPendingFirstMatch(res.firstMatch);
        setEditTarget(res.search);
      } else {
        onSaved(res.search, res.firstMatch);
      }
    } catch (e: unknown) {
      // A server refusal carries a message worth showing (over the cap,
      // unauthorised); a dropped mobile connection carries «Failed to fetch»,
      // which is English, technical and about nothing the user did.
      // At the free cap the server refuses an ИИ-подбор outright — it never
      // retires a search the reader built by hand (notifications.controller).
      // Say so in the reader's language with the cap the API reported, rather
      // than its English `message`.
      const capFromApi =
        e instanceof ApiError && typeof e.details?.['limit'] === 'number'
          ? (e.details['limit'] as number)
          : null;
      const cap = capFromApi ?? savedSearchCapFor(user);
      setSaveError(
        e instanceof ApiError && e.code === 'saved_search_limit'
          ? t('saved.limitReached', { n: Number.isFinite(cap) ? cap : 1 })
          : e instanceof NetworkError
            ? t('onboarding.niche.saveNetworkError')
            : e instanceof Error && e.message
              ? e.message
              : t('aiOnboarding.saveError'),
      );
    } finally {
      setSaving(false);
    }
  }

  // ── The proposal ────────────────────────────────────────────────────────
  if (proposal && draft) {
    return (
      <>
        <h2
          id={titleId}
          className="mb-2 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
        >
          {t('aiOnboarding.step2.title')}
        </h2>
        <AiProposalReview
          response={proposal}
          draft={draft}
          onDraftChange={setDraft}
          onSave={() => void save(false)}
          onEdit={() => void save(true)}
          saving={saving}
          saveError={saveError}
        />
        {/* The standard builder, opened on the row «Изменить» just created. It
            portals above this modal (both are z-50; the later portal wins), so
            the wizard is still underneath when the sheet closes. */}
        <SavedSearchEditSheet
          search={editTarget}
          open={editTarget !== null}
          // No second «Заполнить с ИИ» here — see the prop's own note.
          aiFill={false}
          onOpenChange={(open) => {
            if (open || !editTarget) return;
            const saved = editTarget;
            setEditTarget(null);
            onSaved(saved, pendingFirstMatch);
          }}
        />
      </>
    );
  }

  // ── The five "no proposal" screens (макет 5) ────────────────────────────
  if (ai.phase === 'failed' && !tinUnknown) {
    if (ai.failure === 'ai_not_understood') {
      return (
        <>
          <h2 id={titleId} className="sr-only">
            {t('aiOnboarding.step1.title')}
          </h2>
          <AiNotUnderstoodCard
            // «Уточнить» keeps whatever was typed — re-typing a sentence we
            // just rejected is where people give up.
            onRefine={ai.reset}
            onManual={() => leaveToManual('not_understood')}
          />
          <p className="mt-3 text-center text-[12px] text-ink-muted">
            {t('aiOnboarding.manualFooter')}
          </p>
        </>
      );
    }
    if (ai.failure === 'ai_quota_exhausted') {
      return (
        <>
          <h2 id={titleId} className="sr-only">
            {t('aiOnboarding.step1.title')}
          </h2>
          <AiQuotaOutCard
            quota={ai.quota}
            onManual={() => leaveToManual('quota')}
            onPro={() => navigate('/plans?src=ai_onboarding')}
          />
          <p className="mt-3 text-center text-[12px] text-ink-muted">
            {t('aiOnboarding.manualFooter')}
          </p>
        </>
      );
    }
    // timeout · 502 · network · anything unrecognised. None of them spends an
    // attempt, and the copy says so — see `AiErrorCard`.
    return (
      <>
        <h2 id={titleId} className="sr-only">
          {t('aiOnboarding.step1.title')}
        </h2>
        <AiErrorCard onManual={() => leaveToManual('error')} onRetry={ai.retry} />
        <p className="mt-3 text-center text-[12px] text-ink-muted">
          {t('aiOnboarding.manualFooter')}
        </p>
      </>
    );
  }

  // ── Шаг 1 ───────────────────────────────────────────────────────────────
  const busy = ai.phase === 'loading';
  const ready = canAsk(text, tin);
  return (
    <>
      <h2
        id={titleId}
        className="mb-1 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
      >
        {t('aiOnboarding.step1.title')}
      </h2>
      {tinUnknown ? (
        <p
          role="status"
          className="mb-3 rounded-lg border border-brand/25 bg-brand-dim px-3 py-2 text-[13px] leading-[1.55] text-ink"
        >
          {t('aiOnboarding.tinUnknown.desc')}
        </p>
      ) : (
        <p className="mb-3 text-[13px] leading-[1.55] text-ink-soft">
          {t('aiOnboarding.step1.desc')}
        </p>
      )}

      <textarea
        ref={textRef}
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, AI_MAX_TEXT))}
        rows={3}
        maxLength={AI_MAX_TEXT}
        disabled={busy}
        aria-label={t('aiOnboarding.step1.title')}
        placeholder={t('aiOnboarding.step1.placeholder')}
        className="w-full resize-none rounded-lg border border-edge bg-bone px-3 py-2.5 text-[16px] leading-snug text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand disabled:opacity-60"
      />
      <div className="mt-1.5 flex items-start justify-between gap-3">
        <p className="text-[12px] leading-snug text-ink-muted">{t('aiOnboarding.step1.example')}</p>
        {/* BEFORE the button, never after: someone who spends their last
            attempt on half a sentence was never told it was the last one. */}
        <AiQuotaLine quota={ai.quota} variant="counter" className="shrink-0" />
      </div>

      <div className="my-3 flex items-center gap-3">
        <span className="h-px flex-1 bg-edge" />
        <span className="text-[12px] text-ink-muted">{t('aiOnboarding.step1.or')}</span>
        <span className="h-px flex-1 bg-edge" />
      </div>

      {/* The ИНН door. Cheapest and most verifiable of the three inputs: the
          codes come from the company's OWN past wins, so the model is only
          naming and wording them. */}
      <label className="mb-1 block text-[13px] font-semibold text-ink" htmlFor="ai-onboarding-tin">
        {t('aiOnboarding.step1.tinLabel')}
      </label>
      <input
        id="ai-onboarding-tin"
        type="text"
        inputMode="numeric"
        maxLength={TIN_DIGITS}
        value={tin}
        disabled={busy}
        onChange={(e) => setTin(e.target.value.replace(/\D/g, ''))}
        placeholder={t('company.tinPlaceholder', { digits: TIN_DIGITS })}
        className="h-11 w-full rounded-md border-[1.5px] border-edge bg-bone px-3 text-[16px] text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand disabled:opacity-60"
      />
      <p className="mt-1 text-[12px] text-ink-muted">{t('aiOnboarding.step1.tinHint')}</p>

      <button
        type="button"
        onClick={ask}
        disabled={!ready || busy}
        className={`${aiPrimaryClass} mt-4 h-12 w-full`}
      >
        {busy ? (
          t('aiOnboarding.step1.loading')
        ) : (
          <>
            <AiSparkle className="mr-1.5 text-white" />
            {t('aiOnboarding.step1.submit')}
          </>
        )}
      </button>

      {/* Sonnet answers in 6–10 s, so a spinner alone would read as a hang.
          Skeleton rows say "something is being built" and occupy the space the
          chips will take, which stops the sheet jumping when they land. */}
      {busy && (
        <div className="mt-3 flex flex-col gap-2" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span key={i} className="h-7 w-full animate-pulse rounded-full bg-bone" />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => leaveToManual('link')}
        className="mt-3 w-full text-center text-[13px] font-semibold text-ink-soft underline underline-offset-2 transition-colors hover:text-ink"
      >
        {t('aiOnboarding.step1.manual')}
      </button>
    </>
  );
}
